import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, UserRound, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { Button, Modal, Input, Textarea, PageHeader, EmptyState, ConfirmDialog, Spinner } from '../components/ui';
import type { Profile } from '@conference/shared';
import DraftRecordDrawer from '../components/ingestion/DraftRecordDrawer';
import ReviewTable from '../components/ingestion/ReviewTable';
import { contentIngestionService, type DraftChange } from '../services/contentIngestionService';

// ─── Schema ───────────────────────────────────────────────────────────────────

const speakerSchema = z.object({
  name:      z.string().min(2, 'Name is required'),
  email:     z.string().email('Invalid email'),
  title:     z.string().optional(),
  company:   z.string().optional(),
  bio:       z.string().optional(),
  linkedin:  z.string().optional(),
  portfolio: z.string().optional(),
  avatar_url:z.string().optional(),
  is_featured: z.boolean().default(false),
  featured_order: z.string().optional(),
});
type SpeakerForm = z.input<typeof speakerSchema>;

// ─── Speaker modal ────────────────────────────────────────────────────────────

function SpeakerModal({ open, onClose, speaker, eventId, onSaved, ingestionEnabled = false }: {
  open: boolean; onClose: () => void; speaker: Profile | null; eventId: string; onSaved: () => void; ingestionEnabled?: boolean;
}) {
  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<SpeakerForm>({
    resolver: zodResolver(speakerSchema),
    defaultValues: { name: '', email: '', title: '', company: '', bio: '', linkedin: '', portfolio: '', avatar_url: '', is_featured: false, featured_order: '' },
  });
  const [pendingSave, setPendingSave] = useState<SpeakerForm | null>(null);
  const isFeatured = watch('is_featured');

  useEffect(() => {
    if (open) {
      reset(speaker ? {
        name: speaker.name, email: speaker.email,
        title: speaker.title ?? '', company: speaker.company ?? '',
        bio: speaker.bio ?? '', linkedin: speaker.linkedin ?? '',
        portfolio: speaker.portfolio ?? '', avatar_url: speaker.avatar_url ?? '',
        is_featured: speaker.is_featured ?? false,
        featured_order: speaker.featured_order?.toString() ?? '',
      } : { name: '', email: '', title: '', company: '', bio: '', linkedin: '', portfolio: '', avatar_url: '', is_featured: false, featured_order: '' });
    }
  }, [open, speaker, reset]);

  const persist = async (data: SpeakerForm) => {
    const payload = {
      ...data,
      linkedin:   data.linkedin   || null,
      portfolio:  data.portfolio  || null,
      avatar_url: data.avatar_url || null,
      title:      data.title      || null,
      company:    data.company    || null,
      bio:        data.bio        || null,
      is_featured: data.is_featured ?? false,
      featured_order: data.is_featured && data.featured_order ? Number(data.featured_order) : null,
    };

    try {
      if (ingestionEnabled) {
        const { name, company, ...rest } = payload;
        const content = { ...rest, display_name: name, affiliation: company };
        if (speaker) await contentIngestionService.stagePublishedUpdate(eventId, 'speaker', speaker.id, content);
        else await contentIngestionService.createDraft(eventId, 'speaker', content);
        toast.success('Speaker draft saved for review');
      } else if (speaker) {
        await apiClient.patch(`/speakers/${speaker.id}?event_id=${eventId}`, payload);
        toast.success('Speaker updated');
      } else {
        await apiClient.post('/speakers', { ...payload, event_id: eventId });
        toast.success('Speaker profile created. Remember to invite them to sign in.');
      }
      setPendingSave(null); onSaved(); onClose();
    } catch (err) {
      toast.error(speaker ? 'Update failed' : 'Create failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };
  const prepareSave = (data: SpeakerForm) => speaker ? setPendingSave(data) : persist(data);

  return (
    <Modal open={open} onClose={onClose} title={speaker ? 'Edit Speaker' : 'Add Speaker'} size="lg">
      <form onSubmit={handleSubmit(prepareSave)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Full Name *" placeholder="Dr. Sarah Chen" error={errors.name?.message} {...register('name')} />
          <Input label="Email *" type="email" placeholder="sarah@…" error={errors.email?.message} {...register('email')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Title" placeholder="AI Research Lead" {...register('title')} />
          <Input label="Company / Affiliation" placeholder="OpenAI" {...register('company')} />
        </div>
        <Textarea label="Bio" placeholder="Short speaker biography…" rows={3} {...register('bio')} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="LinkedIn URL" placeholder="https://linkedin.com/in/…" {...register('linkedin')} />
          <Input label="Portfolio / Website" placeholder="https://…" {...register('portfolio')} />
        </div>
        <Input label="Avatar / Photo URL" placeholder="https://images.unsplash.com/…" {...register('avatar_url')} />
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" className="accent-sky-500" {...register('is_featured')} />
            Feature on home page
          </label>
          {isFeatured && <Input label="Featured order" type="number" min="0" placeholder="Optional" {...register('featured_order')} />}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>{speaker ? 'Save Changes' : 'Add Speaker'}</Button>
        </div>
      </form>
      <ConfirmDialog open={pendingSave !== null} onClose={() => setPendingSave(null)} onConfirm={() => pendingSave && persist(pendingSave)} intent="primary" title="Save changes?" description="Apply the current speaker changes?" confirmLabel="Save changes" />
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SpeakersPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [speakers, setSpeakers]   = useState<Profile[]>([]);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modalOpen, setModal]     = useState(false);
  const [editing, setEditing]     = useState<Profile | null>(null);
  const [deleteTarget, setDel]    = useState<Profile | null>(null);
  const [deleting, setDeleting]   = useState(false);
  const [ingestionEnabled, setIngestionEnabled] = useState(false);
  const [view, setView] = useState<'published' | 'draft'>('published');
  const [drafts, setDrafts] = useState<DraftChange[]>([]);
  const [editingDraft, setEditingDraft] = useState<DraftChange | null>(null);

  async function load() {
    if (!eventId) return;
    setLoading(true);
    try {
      const [eventData, speakersData] = await Promise.all([
        apiClient.get<{ id: string; name: string; effective_content_ingestion_enabled?: boolean }>(`/admin/events/${eventId}`),
        apiClient.get<Profile[]>(`/speakers/all?event_id=${eventId}`),
      ]);
      setEventName(eventData.name);
      setIngestionEnabled(Boolean(eventData.effective_content_ingestion_enabled));
      setSpeakers(speakersData);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);

  async function loadDrafts() {
    if (!eventId) return;
    setDrafts(await contentIngestionService.drafts(eventId, { area: 'speakers' }));
  }

  async function refresh() {
    if (view === 'draft') await loadDrafts();
    else await load();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (ingestionEnabled) await contentIngestionService.stagePublishedDelete(eventId ?? '', 'speaker', deleteTarget.id);
      else await apiClient.delete(`/speakers/${deleteTarget.id}?event_id=${eventId}`);
      toast.success(ingestionEnabled ? 'Delete draft created for review' : 'Speaker removed');
      setDel(null);
      await refresh();
    } catch (err) {
      toast.error('Delete failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDeleting(false);
    }
  }

  const filtered = search
    ? speakers.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.company?.toLowerCase().includes(search.toLowerCase()) ||
        s.title?.toLowerCase().includes(search.toLowerCase())
      )
    : speakers;

  return (
    <div className="p-8">
      <PageHeader
        title="Speakers"
        description={`Manage speakers for ${eventName || '…'}`}
        breadcrumb={<Link to="/events" className="hover:text-gray-300">Events</Link>}
        action={<Button icon={<Plus size={14} />} onClick={() => { setEditing(null); setModal(true); }}>Add Speaker</Button>}
      />

      {ingestionEnabled && <div className="mb-5 flex gap-2" role="group" aria-label="Speaker content view"><Button size="sm" variant={view === 'published' ? 'primary' : 'secondary'} onClick={() => setView('published')}>Published</Button><Button size="sm" variant={view === 'draft' ? 'primary' : 'secondary'} onClick={() => { setView('draft'); void loadDrafts(); }}>Draft</Button></div>}

      {/* Search */}
      {view === 'published' && <div className="mb-5">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search speakers…"
          className="w-full max-w-sm px-3.5 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500"
        />
      </div>}

      {view === 'draft' ? <ReviewTable eventId={eventId ?? ''} changes={drafts} onEdit={setEditingDraft} onChanged={loadDrafts} /> : loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={UserRound}
          title="No speakers found"
          description="Add speakers and assign them to sessions."
          action={<Button icon={<Plus size={14} />} onClick={() => setModal(true)}>Add Speaker</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((speaker) => (
            <div key={speaker.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                {speaker.avatar_url ? (
                  <img src={speaker.avatar_url} alt={speaker.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-gray-300">{speaker.name[0]}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{speaker.name}</p>
                  {speaker.title && <p className="text-xs text-gray-400 truncate">{speaker.title}</p>}
                  {speaker.company && <p className="text-xs text-gray-500 truncate">{speaker.company}</p>}
                </div>
              </div>
              {speaker.bio && (
                <p className="text-xs text-gray-400 line-clamp-2 mb-3">{speaker.bio}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {speaker.linkedin && (
                    <a href={speaker.linkedin} target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-sky-400 transition-colors">
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button aria-label={`Edit ${speaker.name}`} variant="ghost" size="sm" icon={<Pencil size={12} />} onClick={() => { setEditing(speaker); setModal(true); }} />
                  <Button aria-label={`Delete ${speaker.name}`} variant="ghost" size="sm" icon={<Trash2 size={12} />} className="hover:text-red-400" onClick={() => setDel(speaker)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SpeakerModal open={modalOpen} onClose={() => setModal(false)} speaker={editing} eventId={eventId ?? ''} ingestionEnabled={ingestionEnabled} onSaved={refresh} />
      <DraftRecordDrawer eventId={eventId ?? ''} change={editingDraft} onClose={() => setEditingDraft(null)} onSaved={() => { setEditingDraft(null); void loadDrafts(); }} />
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDel(null)} onConfirm={handleDelete} loading={deleting}
        title={ingestionEnabled ? 'Stage speaker deletion?' : 'Remove Speaker'}
        description={ingestionEnabled ? `Create a reviewed delete draft for "${deleteTarget?.name}"? The live speaker will remain until publication.` : `Remove "${deleteTarget?.name}" from the system?`}
        confirmLabel={ingestionEnabled ? 'Create delete draft' : 'Remove'}
      />
    </div>
  );
}
